import { useState, useEffect } from 'react';
import domain from '../../../reusecomponent/domain.jsx';
import { getToken } from '../../function/token';
import { faPhone, faEdit, faSave, faTimes, faEnvelope, faMapMarkerAlt, faGlobe, faBriefcase } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConnectWhatsapp, ErrorToastContainer } from '../../function/CustomFunction';
import { useDispatch, useSelector } from 'react-redux';
import { setProfile } from '../../../redux/profile_slice.jsx';


export default function ProfileCard({ profileLoading }) {
  const [profileData, setProfileData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const profile = useSelector((state) => state.profileStore.value);
  const userServices = useSelector((state) => state.serviceStore.value);
  const dispatch = useDispatch();

  // Update profile data when props change
  useEffect(() => {
    if (profile) {
      setProfileData(profile);
      const decodedData = profile || {};
      setEditFormData({
        ...decodedData,
        websites: Array.isArray(decodedData.websites) ? decodedData.websites : []
      });
    }
  }, [profile]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (selectedImagePreview) {
        URL.revokeObjectURL(selectedImagePreview);
      }
    };
  }, [selectedImagePreview]);

  const handleEditProfile = async () => {
    try {
      setUpdating(true);
      const token = getToken();
      const headers = new Headers();
      headers.append('Authorization', `Bearer ${token}`);
      headers.append('Content-Type', 'application/json');

      const currentData = profileData?.data || profileData || {};
      const changedData = {};

      // Helper function to normalize values for comparison
      const normalizeValue = (value) => {
        if (value === null || value === undefined || value === '') return '';
        return String(value).trim();
      };

      // Check each field and only include if it has changed
      const currentName = normalizeValue(currentData.name || currentData.profile_name);
      const editName = normalizeValue(editFormData.name);
      // Only allow name changes if new_display_name is null
      if (editName !== currentName && currentData.new_display_name === null) {
        changedData.display_name = editFormData.name; // Use display_name key instead of name
      }

      const currentPhone = normalizeValue(currentData.display_phone_number);
      const editPhone = normalizeValue(editFormData.display_phone_number);
      if (editPhone !== currentPhone) {
        changedData.display_phone_number = editFormData.display_phone_number;
      }

      const currentDescription = normalizeValue(currentData.description);
      const editDescription = normalizeValue(editFormData.description);
      if (editDescription !== currentDescription) {
        changedData.description = editFormData.description;
      }

      const currentAddress = normalizeValue(currentData.address);
      const editAddress = normalizeValue(editFormData.address);
      if (editAddress !== currentAddress) {
        changedData.address = editFormData.address;
      }

      const currentEmail = normalizeValue(currentData.email);
      const editEmail = normalizeValue(editFormData.email);
      if (editEmail !== currentEmail) {
        changedData.email = editFormData.email;
      }

      const currentWebsites = Array.isArray(currentData.websites) ? currentData.websites : [];
      const editWebsites = Array.isArray(editFormData.websites) ? editFormData.websites : [];
      if (JSON.stringify(editWebsites) !== JSON.stringify(currentWebsites)) {
        changedData.websites = editFormData.websites;
      }

      if (editFormData.profile_picture_handle) {
        changedData.profile_picture_handle = editFormData.profile_picture_handle;
      }

      // Include messaging_product if it exists and has changed
      const currentMessagingProduct = normalizeValue(currentData.messaging_product);
      const editMessagingProduct = normalizeValue(editFormData.messaging_product);
      if (editFormData.messaging_product && editMessagingProduct !== currentMessagingProduct) {
        changedData.messaging_product = editFormData.messaging_product;
      }

      // Only make API call if there are changes
      if (Object.keys(changedData).length === 0) {
        setIsEditing(false);
        setUpdating(false);
        return;
      }
      const response = await fetch(`${domain()}/whatsapp-profile`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(changedData),
        redirect: 'follow'
      });

      const result = await response.json();
      if (result.status) {
        let decodedData = JSON.parse(atob(result.data));
        dispatch(setProfile(decodedData));
        setIsEditing(false);
      } else {
        ErrorToastContainer(result.message);
      }
    } catch (error) {
      ErrorToastContainer('Error updating profile. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const startEditing = () => {
    const currentData = profileData?.data || profileData || {};
    setEditFormData({
      name: currentData.name || currentData.profile_name || '',
      display_phone_number: currentData.display_phone_number || '',
      description: currentData.description || '',
      address: currentData.address || '',
      email: currentData.email || '',
      websites: Array.isArray(currentData.websites) ? currentData.websites : [],
      messaging_product: currentData.messaging_product,
      profile_picture_handle: currentData.profile_picture_handle,
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    // Clean up preview URL if it exists
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
      setSelectedImagePreview(null);
    }
  };

  // Handle image file selection and show preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create preview URL for the selected image
    const previewUrl = URL.createObjectURL(file);
    setSelectedImagePreview(previewUrl);
    setImageUploadError(null);

    // Upload the image immediately
    uploadImage(file);
  };

  // Upload image function
  const uploadImage = async (file) => {
    setImageUploading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${domain()}/uplode-file`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      const result = await response.json();
      if (response.ok && result.status && result.data) {
        setEditFormData(prev => ({
          ...prev,
          profile_picture_handle: result.data,
        }));
        // Clear the preview since we now have the uploaded image URL
        if (selectedImagePreview) {
          URL.revokeObjectURL(selectedImagePreview);
          setSelectedImagePreview(null);
        }
      } else {
        setImageUploadError(result.message || 'Failed to upload image');
      }
    } catch (error) {
      setImageUploadError('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  // Helper to get the correct image URL for preview
  const getProfileImageUrl = (img) => {
    if (!img) return "https://static.vecteezy.com/system/resources/previews/018/930/564/non_2x/whatsapp-logo-whatsapp-icon-whatsapp-transparent-free-png.png";
    return img;
  };

  const handleConnectWhatsApp = async () => {
    try {
      setConnectLoading(true);
      await ConnectWhatsapp(setConnectLoading);
    } catch (error) {
    } finally {
      setConnectLoading(false);
    }
  };

  // Modern Loader Component
  const ModernLoader = () => (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      boxShadow: '0 4px 24px 0 rgba(60,72,88,0.10)',
      padding: '28px 20px',
      color: '#222',
      minHeight: 340,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 20,
    }}>
      <div style={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
      }}></div>
      <div style={{
        width: '60%',
        height: 20,
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: 4,
      }}></div>
      <div style={{
        width: '80%',
        height: 16,
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: 4,
      }}></div>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  // Check if WhatsApp needs to be connected first
  if (!userServices?.isconnected) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 24px 0 rgba(60,72,88,0.10)',
        padding: '28px 20px',
        color: '#222',
        minHeight: 340,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        textAlign: 'center',
      }}>
        <div style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: '#f0f8ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 40,
          color: '#25d366',
          border: '3px solid #25d366',
        }}>
          📱
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#333' }}>
          Please Connect WhatsApp
        </div>
        <div style={{ fontSize: 14, color: '#666', maxWidth: 280 }}>
          Connect your WhatsApp Business account to start managing your profile and sending messages.
        </div>
        <button
          onClick={handleConnectWhatsApp}
          disabled={connectLoading}
          style={{
            background: connectLoading ? '#ccc' : '#25d366',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '12px 24px',
            fontWeight: 600,
            fontSize: 14,
            cursor: connectLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s',
            opacity: connectLoading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => !connectLoading && (e.target.style.background = '#128c7e')}
          onMouseLeave={(e) => !connectLoading && (e.target.style.background = '#25d366')}
        >
          {connectLoading ? (
            <div style={{
              width: 16,
              height: 16,
              border: '2px solid #fff',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          ) : (
            <span>🔗</span>
          )}
          {connectLoading ? 'Connecting...' : 'Connect WhatsApp'}
        </button>
      </div>
    );
  }

  // Loading State
  if (profileLoading) {
    return <ModernLoader />;
  }

  const data = profileData?.data || profileData || {};
  const hasAddress = Boolean(data.address && data.address.trim());
  const hasEmail = Boolean(data.email && data.email.trim());
  const hasWebsite1 = Boolean(data.websites?.[0] && String(data.websites[0]).trim());
  const hasWebsite2 = Boolean(data.websites?.[1] && String(data.websites[1]).trim());
  const hasOtherInfo = hasAddress || hasEmail || hasWebsite1 || hasWebsite2;

  return (
    <div
      className="profile-card-container card border-0 shadow-sm position-relative"
      style={{
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        padding: '30px 24px',
        color: '#1e293b',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        position: 'relative'
      }}
    >
      {/* Edit Button */}
      <button
        onClick={isEditing ? handleEditProfile : startEditing}
        disabled={updating}
        title={isEditing ? "Save Profile" : "Edit Profile"}
        style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: isEditing ? '#075E54' : 'transparent',
          border: 'none',
          fontSize: '15px',
          color: isEditing ? '#ffffff' : '#64748b',
          cursor: updating ? 'not-allowed' : 'pointer',
          padding: '8px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          width: '36px',
          height: '36px',
          opacity: updating ? 0.6 : 1,
        }}
        onMouseEnter={(e) => !isEditing && !updating && (e.target.style.backgroundColor = '#f1f5f9')}
        onMouseLeave={(e) => !isEditing && !updating && (e.target.style.backgroundColor = 'transparent')}
      >
        {updating ? (
          <div style={{
            width: 16,
            height: 16,
            border: '2px solid #fff',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
        ) : (
          <FontAwesomeIcon icon={isEditing ? faSave : faEdit} />
        )}
      </button>

      {/* Cancel Button (only when editing) */}
      {isEditing && (
        <button
          onClick={cancelEditing}
          disabled={updating}
          title="Cancel Editing"
          style={{
            position: 'absolute',
            top: '16px',
            right: '58px',
            background: '#ef4444',
            border: 'none',
            fontSize: '15px',
            color: '#fff',
            cursor: updating ? 'not-allowed' : 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            opacity: updating ? 0.6 : 1,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}

      {/* Top Profile Header (Centered Avatar & Details) */}
      <div className="d-flex flex-column align-items-center text-center w-100">
        {/* Profile Image */}
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <img
            src={
              isEditing
                ? (selectedImagePreview || getProfileImageUrl(editFormData.profile_picture_url || data.profile_picture_url))
                : getProfileImageUrl(data.profile_picture_url)
            }
            alt="Profile Avatar"
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: '3.5px solid #075E54',
              background: '#f8fafc',
              boxShadow: '0 4px 14px rgba(7, 94, 84, 0.2)'
            }}
          />
          {isEditing && (
            <>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-image-upload"
                onChange={handleImageChange}
                disabled={imageUploading}
              />
              <label htmlFor="profile-image-upload" style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                background: imageUploading ? '#ccc' : '#075E54',
                color: '#fff',
                borderRadius: '50%',
                width: '34px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: imageUploading ? 'not-allowed' : 'pointer',
                border: '2px solid #fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                fontSize: '14px',
                zIndex: 2
              }}>
                {imageUploading ? (
                  <div style={{
                    width: 14,
                    height: 14,
                    border: '2px solid #fff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                ) : (
                  <FontAwesomeIcon icon={faEdit} />
                )}
              </label>
              {imageUploading && <div style={{ color: '#10b981', fontSize: 12, marginTop: 4 }}>Uploading...</div>}
              {imageUploadError && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{imageUploadError}</div>}
            </>
          )}
        </div>

        {/* Profile Name */}
        {isEditing ? (
          <div style={{ width: '100%', marginBottom: 12, textAlign: 'left' }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#475569', fontSize: 13, textAlign: 'left' }}>
              Business Display Name
            </label>
            <input
              type="text"
              value={editFormData.name || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={data.new_display_name !== null && data.new_display_name !== undefined ? "Name cannot be updated" : "Enter business name..."}
              disabled={updating || (data.new_display_name !== null && data.new_display_name !== undefined)}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                fontSize: 14,
                backgroundColor: updating || (data.new_display_name !== null && data.new_display_name !== undefined) ? '#f1f5f9' : '#fff',
                cursor: updating || (data.new_display_name !== null && data.new_display_name !== undefined) ? 'not-allowed' : 'text',
                opacity: data.new_display_name !== null && data.new_display_name !== undefined ? 0.7 : 1,
              }}
            />
            {data.new_display_name !== null && data.new_display_name !== undefined && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, textAlign: 'left' }}>
                Name cannot be updated as it has been verified by WhatsApp Business.
              </div>
            )}
          </div>
        ) : (
          (data.name || data.profile_name) && (
            <div className="mb-1">
              <h5 className="fw-bold text-dark mb-1" style={{ fontSize: '19px', letterSpacing: '-0.2px' }}>
                {data.name || data.profile_name}
                {(data.name_status === 'APPROVED' || (data.new_display_name !== null && data.new_display_name !== undefined)) && (
                  <span className="ms-2 badge text-success border fw-normal" style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: '#ecfdf5', borderColor: '#a7f3d0' }}>
                    ✓ {data.name_status === 'APPROVED' ? 'Approved' : 'Verified'}
                  </span>
                )}
              </h5>
            </div>
          )
        )}

        {/* Phone Number */}
        {(data.display_phone_number || data.phone) && (
          <div className="d-flex align-items-center justify-content-center gap-2 mb-2" style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
            <FontAwesomeIcon icon={faPhone} style={{ fontSize: '13px', color: '#94a3b8' }} />
            <span>{data.display_phone_number || data.phone}</span>
          </div>
        )}

        {/* Description / Bio */}
        {isEditing ? (
          <div style={{ width: '100%', marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#475569', fontSize: 13, textAlign: 'left' }}>
              Description
            </label>
            <textarea
              value={editFormData.description || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter profile description..."
              disabled={updating}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                fontSize: 13.5,
                minHeight: 65,
                resize: 'vertical',
                fontFamily: 'inherit',
                backgroundColor: updating ? '#f1f5f9' : '#fff',
                cursor: updating ? 'not-allowed' : 'text',
              }}
            />
          </div>
        ) : (
          data.description ? (
            <p className="text-secondary small mb-1 px-2" style={{ fontSize: '13.5px', lineHeight: '1.5', maxWidth: '340px', color: '#475569', overflowWrap: 'anywhere' }}>
              {data.description}
            </p>
          ) : null
        )}
      </div>

      {/* Divider */}
      <div style={{ width: '100%', borderTop: '1px solid #e2e8f0', margin: '18px 0' }}></div>

      {/* Other Section */}
      <div className="w-100 text-start">
        <h6 className="fw-bold text-dark mb-3" style={{ fontSize: '14px' }}>Other</h6>

        {/* Address */}
        {isEditing ? (
          <div style={{ width: '100%', marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#475569', fontSize: 12.5 }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 6 }} /> Address
            </label>
            <input
              type="text"
              value={editFormData.address || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Enter address..."
              disabled={updating}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                fontSize: 13.5,
                backgroundColor: updating ? '#f1f5f9' : '#fff',
                cursor: updating ? 'not-allowed' : 'text',
              }}
            />
          </div>
        ) : (
          <div className="d-flex align-items-start gap-3 mb-3">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="mt-1 flex-shrink-0" style={{ color: '#64748b', fontSize: '15px' }} />
            {hasAddress ? (
              <span className="fw-semibold text-dark small" style={{ fontSize: '13.5px', lineHeight: '1.4', color: '#334155', overflowWrap: 'anywhere' }}>
                {data.address}
              </span>
            ) : (
              <span className="text-muted small" style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                Not provided
              </span>
            )}
          </div>
        )}

        {/* Email */}
        {isEditing ? (
          <div style={{ width: '100%', marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#475569', fontSize: 12.5 }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6 }} /> Email
            </label>
            <input
              type="email"
              value={editFormData.email || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Enter email..."
              disabled={updating}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                fontSize: 13.5,
                backgroundColor: updating ? '#f1f5f9' : '#fff',
                cursor: updating ? 'not-allowed' : 'text',
              }}
            />
          </div>
        ) : (
          <div className="d-flex align-items-center gap-3 mb-3">
            <FontAwesomeIcon icon={faEnvelope} className="flex-shrink-0" style={{ color: '#64748b', fontSize: '15px' }} />
            {hasEmail ? (
              <span className="fw-semibold text-dark small text-truncate" style={{ fontSize: '13.5px', color: '#334155', overflowWrap: 'anywhere' }}>
                {data.email}
              </span>
            ) : (
              <span className="text-muted small" style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                Not provided
              </span>
            )}
          </div>
        )}

        {/* Website Links */}
        {isEditing ? (
          <>
            <div style={{ width: '100%', marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#475569', fontSize: 12.5 }}>
                <FontAwesomeIcon icon={faGlobe} style={{ marginRight: 6 }} /> Primary Website
              </label>
              <input
                type="url"
                value={editFormData.websites?.[0] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditFormData(prev => {
                    const updated = [...(prev.websites || [])];
                    updated[0] = value;
                    return {
                      ...prev,
                      websites: updated
                    };
                  });
                }}
                placeholder="Enter website URL..."
                disabled={updating}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 13.5,
                  backgroundColor: updating ? '#f1f5f9' : '#fff',
                  cursor: updating ? 'not-allowed' : 'text',
                }}
              />
            </div>
            <div style={{ width: '100%', marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#475569', fontSize: 12.5 }}>
                <FontAwesomeIcon icon={faGlobe} style={{ marginRight: 6 }} /> Secondary Website
              </label>
              <input
                type="url"
                value={editFormData.websites?.[1] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditFormData(prev => {
                    const updated = [...(prev.websites || [])];
                    updated[1] = value;
                    return {
                      ...prev,
                      websites: updated
                    };
                  });
                }}
                placeholder="Enter website URL..."
                disabled={updating}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 13.5,
                  backgroundColor: updating ? '#f1f5f9' : '#fff',
                  cursor: updating ? 'not-allowed' : 'text',
                }}
              />
            </div>
          </>
        ) : (
          <>
            {/* Primary Website */}
            <div className="d-flex align-items-center gap-3 mb-3">
              <FontAwesomeIcon icon={faGlobe} className="flex-shrink-0" style={{ color: '#64748b', fontSize: '15px' }} />
              {hasWebsite1 ? (
                <a
                  href={data.websites[0].startsWith('http://') || data.websites[0].startsWith('https://') ? data.websites[0] : `https://${data.websites[0]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-semibold text-decoration-none small text-truncate"
                  style={{ color: '#0ea5e9', fontSize: '13.5px', overflowWrap: 'anywhere' }}
                >
                  {data.websites[0]}
                </a>
              ) : (
                <span className="text-muted small" style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                  Not provided
                </span>
              )}
            </div>

            {/* Secondary Website (if available) */}
            {hasWebsite2 && (
              <div className="d-flex align-items-center gap-3 mb-2">
                <FontAwesomeIcon icon={faGlobe} className="flex-shrink-0" style={{ color: '#64748b', fontSize: '15px' }} />
                <a
                  href={data.websites[1].startsWith('http://') || data.websites[1].startsWith('https://') ? data.websites[1] : `https://${data.websites[1]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-semibold text-decoration-none small text-truncate"
                  style={{ color: '#0ea5e9', fontSize: '13.5px', overflowWrap: 'anywhere' }}
                >
                  {data.websites[1]}
                </a>
              </div>
            )}
            {/* Category / Vertical */}
            {data.vertical && (
              <div className="d-flex align-items-center gap-3 mb-2">
                <FontAwesomeIcon icon={faBriefcase} className="flex-shrink-0" style={{ color: '#64748b', fontSize: '15px' }} />
                <span className="fw-semibold text-dark small" style={{ fontSize: '13.5px', color: '#334155' }}>
                  {{
                    'EDU': 'Education',
                    'OTHER': 'Other',
                    'AUTO': 'Automotive',
                    'BEAUTY': 'Beauty, Spa & Salon',
                    'APPAREL': 'Clothing & Apparel',
                    'ENTERTAIN': 'Entertainment',
                    'EVENT_PLAN': 'Event Planning',
                    'FINANCE': 'Finance & Banking',
                    'GROCERY': 'Grocery',
                    'GOVT': 'Government & Public Services',
                    'HOTEL': 'Hotel & Lodging',
                    'HEALTH': 'Medical & Health',
                    'NONPROFIT': 'Non-profit',
                    'PROF_SERVICES': 'Professional Services',
                    'RETAIL': 'Retail',
                    'TRAVEL': 'Travel & Transportation',
                    'RESTAURANT': 'Restaurant',
                  }[data.vertical] || data.vertical}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .profile-card-container {
            padding: 20px 16px !important;
            border-radius: 14px !important;
          }
        }
      `}</style>
    </div>
  );
} 